import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { mailIngestApi } from '@/services/api';
import { emitAppEvent } from '@/utils/appEvents';
import type { MailIngestStagingRow } from '@/types';

/**
 * Mail-Eingang-Staging (Admin ≥ Einstellungen-Tab „Mail-Eingang"): Anhänge,
 * die keine automatische Rechnung erzeugt haben ('no_invoice'/'failed'),
 * sowie alle übrigen Staging-Zeilen zum Einsehen. „Erneut extrahieren"
 * schickt den Anhang zurück in die extraction_jobs-Queue (der Worker zieht
 * ihn im nächsten Loop).
 */

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Alle Status' },
  { value: 'no_invoice', label: 'Ohne Rechnung (fehlt etwas)' },
  { value: 'failed', label: 'Fehlgeschlagen' },
  { value: 'pending', label: 'In Warteschlange' },
  { value: 'processing', label: 'In Verarbeitung' },
  { value: 'done', label: 'Erledigt' },
  { value: 'ignored', label: 'Ignoriert' },
];

const STATUS_BADGES: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-700',
  processing: 'bg-blue-100 text-blue-800',
  done: 'bg-green-100 text-green-800',
  no_invoice: 'bg-amber-100 text-amber-800',
  failed: 'bg-red-100 text-red-800',
  ignored: 'bg-gray-100 text-gray-500',
};

const CONFIDENCE_LABELS: Record<string, string> = {
  projektnummer: 'Projektnummer',
  name: 'Name (exakt)',
  name_teil: 'Name (teilweise)',
  keine: 'Keine',
};

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    pending: 'In Warteschlange',
    processing: 'In Verarbeitung',
    done: 'Erledigt',
    no_invoice: 'Ohne Rechnung',
    failed: 'Fehlgeschlagen',
    ignored: 'Ignoriert',
  };
  return map[status] ?? status;
}

/** Retry-Restriktion spiegelt MailIngestService::retry(). */
function canRetry(row: Pick<MailIngestStagingRow, 'status' | 'file_size' | 'extraction_job_id'>): boolean {
  return (row.status === 'failed' || row.status === 'no_invoice') && row.extraction_job_id != null;
}

export function MailIngestTab() {
  const [rows, setRows] = useState<MailIngestStagingRow[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage] = useState(25);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [retryingId, setRetryingId] = useState<number | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const load = (targetPage = page) => {
    setIsLoading(true);
    setError('');
    mailIngestApi
      .listStaging({ status: statusFilter || undefined, page: targetPage, per_page: perPage })
      .then((response) => {
        setRows(response.data.data);
        setTotal(response.data.pagination?.total ?? response.data.data.length);
        setPage(targetPage);
      })
      .catch((err) => setError(err?.response?.data?.error || 'Staging-Liste konnte nicht geladen werden'))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => { load(1); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleRetry = async (row: MailIngestStagingRow) => {
    if (!window.confirm(`Anhang „${row.original_name ?? 'unbenannt'}" erneut extrahieren? Der Worker verarbeitet ihn im nächsten Durchlauf.`)) return;
    setRetryingId(row.id);
    setError('');
    setMessage('');
    try {
      await mailIngestApi.retry(row.id);
      setMessage(`„${row.original_name ?? 'Anhang'}" wartet wieder in der Warteschlange.`);
      emitAppEvent('rechnungen:changed');
      await load(page);
    } catch (err) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Erneut-Extrahieren');
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          Mail-Eingang Staging
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Anhänge aus rechnung@gpsbau.de mit Status ihrer automatischen Verarbeitung. Einträge
          „Ohne Rechnung" oder „Fehlgeschlagen" können erneut extrahiert werden — der lokale
          Worker liest sie beim nächsten Lauf erneut.
        </p>
      </div>

      {/* Status-Filter */}
      <div className="flex items-center gap-2">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input max-w-xs"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {message && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
          {message}
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Lade Staging-Einträge…
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">Keine Staging-Einträge für diesen Filter.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Anhang</th>
                <th className="px-3 py-2">Absender / Betreff</th>
                <th className="px-3 py-2">Eingang</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Begründung</th>
                <th className="px-3 py-2">Ergebnis</th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <tr key={row.id} className="align-top hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <span className="font-medium text-gray-900">{row.original_name ?? '—'}</span>
                    {row.file_size != null && (
                      <span className="block text-xs text-gray-400">
                        {Math.max(1, Math.round(row.file_size / 1024))} KB
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-gray-900">{row.sender_name || row.sender_email || '—'}</span>
                    {row.sender_name && row.sender_email && (
                      <span className="block text-xs text-gray-400">{row.sender_email}</span>
                    )}
                    {row.subject && (
                      <span className="block max-w-xs truncate text-xs text-gray-500" title={row.subject}>
                        {row.subject}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-500">{row.received_at}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGES[row.status] ?? 'bg-gray-100 text-gray-700'}`}>
                      {statusLabel(row.status)}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {row.skip_reason || row.error_message ? (
                      <span className="block max-w-xs truncate text-xs text-red-700" title={row.skip_reason ?? row.error_message ?? undefined}>
                        {row.skip_reason ?? row.error_message}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {row.invoice_id ? (
                      <span className="text-xs text-gray-700">
                        Rechnung {row.invoice_number ?? `#${row.invoice_id}`}
                      </span>
                    ) : row.matched_bauvorhaben_name ? (
                      <span className="text-xs text-gray-700">
                        {row.matched_bauvorhaben_name}
                        {row.match_confidence && (
                          <span className="block text-xs text-gray-400">
                            Treffer: {CONFIDENCE_LABELS[row.match_confidence] ?? row.match_confidence}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canRetry(row) && (
                      <button
                        onClick={() => handleRetry(row)}
                        disabled={retryingId === row.id}
                        className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-primary-600 hover:text-primary-700 disabled:opacity-50"
                        title="Neue Extraktion in die Warteschlange stellen"
                      >
                        {retryingId === row.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5" />
                        )}
                        Erneut extrahieren
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          Seite {page} von {totalPages} ({total} Einträge)
          <button
            onClick={() => load(Math.max(1, page - 1))}
            disabled={page <= 1 || isLoading}
            className="rounded border px-2 py-1 disabled:opacity-50"
          >
            ‹
          </button>
          <button
            onClick={() => load(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages || isLoading}
            className="rounded border px-2 py-1 disabled:opacity-50"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}